/** @jsx isolateComponent */

import { css } from '@emotion/core';
import { useState } from 'react';

import { defaultCurrency } from '@zapier/currency';
import { Colors, Modal } from '@zapier/design-system';
import { responsiveStyles } from '@zapier/responsive-utils';
import { isolateComponent } from '@zapier/style-encapsulation';

import { Error } from '@zapier/upsells/src/components';
import { useAutoUpgrade, useSize } from '@zapier/upsells/src/hooks';
import { ApolloInjection, Size } from '@zapier/upsells/src/types';

import {
  AutoUpgradeCelebrateView,
  AutoUpgradeNudgeView,
  AutoUpgradeOptInView,
} from '.';

type AutoUpgradeModalView = 'celebrate' | 'error' | 'nudge' | 'optIn';

// Can't use default ModalContent because it doesn't support changing padding.
const modalContentStyles = (modalView: AutoUpgradeModalView, size: Size) =>
  responsiveStyles(size, {
    small: css`
      background-color: ${Colors.white};
      border-radius: 0;
      overflow: ${modalView === 'nudge'
        ? 'hidden' // Scroll bar messes up some of the nudge images.
        : 'auto'};
      padding: 30px;
      width: 100%;
    `,
    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
    medium: css`
      border-radius: 30px;
      box-shadow: 0px 20px 30px 0px rgba(0, 0, 0, 0.5);
      margin: 30px;
      max-height: min(calc(100vh - 80px - 60px), 800px);
      max-width: 750px;
      padding: ${modalView === 'nudge'
        ? 0 // Nudge state has an image that goes all the way to the modal edge
        : 30};
      width: auto;
    `,
  });

export type AutoUpgradeModalProps = {
  defaultView?: AutoUpgradeModalView;
  onClose: () => void;
  onOptInSuccess?: () => void;
  /**
   * If true, a success modal view will appear if opt-in succeeds. If false, no modal view will appear on success.
   */
  showCelebrationOnOptInSuccess?: boolean;
} & ApolloInjection;

export const AutoUpgradeModal = ({
  defaultView = 'nudge',
  useMutation,
  useQuery,
  onClose,
  onOptInSuccess,
  showCelebrationOnOptInSuccess = true,
}: AutoUpgradeModalProps) => {
  const size = useSize();
  const [modalView, setModalView] = useState(defaultView);

  const {
    account,
    autoUpgradeToPlan,
    error,
    isEligibleForAutoUpgrade,
    isLoading,
    setAutoUpgrade,
  } = useAutoUpgrade({
    onSetAutoUpgradeFailure: () => setModalView('error'),
    onSetAutoUpgradeSuccess: () => {
      if (onOptInSuccess) {
        onOptInSuccess();
      }

      if (showCelebrationOnOptInSuccess) {
        setModalView('celebrate');
      } else {
        onClose();
      }
    },
    useMutation,
    useQuery,
  });

  // Only USD payments are eligible for auto upgrade.
  const usd = defaultCurrency;

  if (
    !isEligibleForAutoUpgrade &&
    modalView !== 'celebrate' // User won't be eligible after opting in, but we still want them to see the celebration view.
  ) {
    return null;
  }

  const navigateToOptInView = () => setModalView('optIn');

  const onOptInClick = () => {
    setAutoUpgrade(true);
  };

  const contentMap = {
    celebrate: !autoUpgradeToPlan ? null : (
      <AutoUpgradeCelebrateView
        autoUpgradeToPlan={autoUpgradeToPlan}
        onClose={onClose}
      />
    ),
    error: (
      <Error
        eventName="auto_upgrade_modal_error_view"
        fullStoryEventName="Viewed Auto Upgrade Modal - Error View"
        message={
          error ??
          "Please try again. If the problem persists, click the button below and we'll help you through it."
        }
      />
    ),
    nudge: (
      <AutoUpgradeNudgeView
        onAcceptClick={navigateToOptInView}
        onDeclineClick={onClose}
      />
    ),
    optIn:
      !account || !autoUpgradeToPlan ? null : (
        <AutoUpgradeOptInView
          account={account}
          autoUpgradeToPlan={autoUpgradeToPlan}
          currency={usd}
          onAcceptClick={onOptInClick}
        />
      ),
  };

  return isLoading ? null : (
    <Modal onClosed={onClose}>
      <div css={modalContentStyles(modalView, size)}>
        {contentMap[modalView]}
      </div>
    </Modal>
  );
};
